import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { displayNameOf, useAuth } from '../auth/AuthProvider';
import { Wordmark } from '../components/Clapper';
import { useGo } from '../components/Nav';
import { ProfilePicture } from '../components/ProfilePicture';
import { LinkIcon, RecordIcon, WaveformIcon } from '../components/icons';
import { Button, ErrorText, Input, cx } from '../components/ui';
import appIcon from '../assets/slate-app-icon.png';
import macPanel from '../assets/mac-panel@2x.png';
import { track } from '../lib/amplitude';
import { errorMessage } from '../lib/api';
import { completeOnboarding, fetchMe, updateMe, type Me } from '../queries/account';

/**
 * The first thing a new account sees, in two steps.
 *
 * One: who you are, a name and a picture, so the recordings you are about
 * to make carry a face. Two: how Slate works, in three lines, and where to
 * get it, which depends on what they are reading this on: the app for this
 * machine first, then the others. No plan, no team, no card: those come
 * up in the product at the moment a paid feature would help, which is
 * where people actually decide. Someone who arrived through an
 * invitation still gets both steps; the second is worth their minute.
 *
 * Whether the steps are done lives on the account, not in this browser:
 * the app sends a person here until they are. `?next=` carries whatever
 * they were on their way to.
 */

function nextPath(): string {
  const next = new URLSearchParams(window.location.search).get('next');
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
}

type Step = 'profile' | 'explainer';

export function WelcomePage() {
  const { user } = useAuth();
  const go = useGo();
  const queryClient = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const [step, setStep] = useState<Step>('profile');
  const first = displayNameOf(user).split(' ')[0];

  useEffect(() => {
    track('Viewed Welcome Page');
  }, []);

  // The answer from the server is the new account row, and it has to go
  // into the cache before we navigate. The guard that sends people here
  // reads the same cached row, so leaving it stale bounced them straight
  // back to step one.
  const finish = useMutation({
    mutationFn: completeOnboarding,
    onSuccess: (next) => {
      track('Onboarding Completed');
      queryClient.setQueryData(['me'], next);
      go(nextPath(), { replace: true });
    },
  });

  // Already done, or done in another tab: nothing to ask.
  useEffect(() => {
    if (me.data?.onboardedAt) go(nextPath(), { replace: true });
  }, [go, me.data?.onboardedAt]);

  if (!me.data) return null;

  return (
    <div className="min-h-full bg-slate-950 px-4 sm:px-6">
      <div className="mx-auto w-full max-w-xl py-8 sm:py-12">
      <div className="mb-8">
        <Wordmark />
      </div>
      <ol className="mb-6 flex items-center gap-3 text-caption text-chalk-faint" aria-label="Steps">
        <StepMark n={1} label="You" active={step === 'profile'} done={step === 'explainer'} />
        <span className="h-px w-6 bg-hairline" aria-hidden />
        <StepMark n={2} label="How it works" active={step === 'explainer'} done={false} />
      </ol>

      {step === 'profile' ? (
        <ProfileStep me={me.data} first={first} onDone={() => setStep('explainer')} />
      ) : (
        <ExplainerStep
          onDone={() => finish.mutate()}
          busy={finish.isPending}
          error={finish.error ? errorMessage(finish.error) : null}
        />
      )}
      </div>
    </div>
  );
}

function StepMark({ n, label, active, done }: { n: number; label: string; active: boolean; done: boolean }) {
  return (
    <li className={cx('flex items-center gap-2', active && 'text-chalk')}>
      <span
        className={cx(
          'flex h-5 w-5 items-center justify-center rounded-full border text-[11px] tnum',
          active ? 'border-chalk text-chalk' : done ? 'border-go bg-go/20 text-go' : 'border-hairline',
        )}
      >
        {done ? '✓' : n}
      </span>
      {label}
    </li>
  );
}

function ProfileStep({ me, first, onDone }: { me: Me; first: string; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(me.name ?? '');
  const save = useMutation({
    mutationFn: () => updateMe({ name: name.trim() || null }),
    onSuccess: (next) => {
      track('Profile Updated', { field: 'name' });
      queryClient.setQueryData(['me'], next);
      onDone();
    },
  });
  const changed = (me.name ?? '') !== name.trim();

  return (
    <section className="card p-5 sm:p-6">
      <p className="text-caption uppercase tracking-wide text-chalk-faint">Welcome{first ? `, ${first}` : ''}</p>
      <h1 className="mt-2 text-h2 text-chalk">How people will see you</h1>
      <div className="mt-5">
        <ProfilePicture me={me} />
      </div>
      <form
        className="mt-5 space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (changed) save.mutate();
          else onDone();
        }}
      >
        <div className="max-w-xs">
          <Input label="Name" value={name} maxLength={80} autoFocus onChange={(event) => setName(event.target.value)} />
        </div>
        {save.isError ? <ErrorText>{errorMessage(save.error)}</ErrorText> : null}
        <Button type="submit" variant="primary" size="lg" busy={save.isPending} disabled={!name.trim()}>
          Continue
        </Button>
      </form>
    </section>
  );
}

/**
 * Which app to offer first. The desktop apps are the good ones, so a Mac or a
 * PC gets its own; a phone or anything else is told what is true, which is
 * that the browser it is already using records.
 */
type Here = 'mac' | 'windows' | 'other';

function here(): Here {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod|Android/.test(ua)) return 'other';
  if (/Windows/.test(ua)) return 'windows';
  if (/Macintosh|Mac OS X/.test(ua)) return 'mac';
  return 'other';
}

/** The long form, with every platform and what each one can do. */
const DOWNLOADS = 'https://slatevideo.com/download';

const DESKTOP: Record<'mac' | 'windows', { title: string; body: string; cta: string }> = {
  mac: {
    title: 'Slate for Mac',
    body: 'Lives in the menu bar. Records system audio, transcribes on your Mac, and uploads while you talk.',
    cta: 'Download for Mac',
  },
  windows: {
    title: 'Slate for Windows',
    body: 'Lives in the system tray, by the clock. Records system audio and uploads while you talk.',
    cta: 'Download for Windows',
  },
};

const HOW = [
  {
    icon: <RecordIcon size={18} />,
    tone: 'text-record',
    ring: 'bg-record/15',
    title: 'Record',
    body: 'Menu bar, tray, browser or phone. Pause and resume; the pause is not in the video.',
    pulse: true,
  },
  {
    icon: <LinkIcon size={18} />,
    tone: 'text-signal',
    ring: 'bg-signal/15',
    title: 'Stop, and the link is ready',
    body: 'On your clipboard before the upload finishes. Send it anywhere.',
    pulse: false,
  },
  {
    icon: <WaveformIcon size={18} />,
    tone: 'text-go',
    ring: 'bg-go/15',
    title: 'The rest is done for you',
    body: 'Title, summary, captions, and the pages you showed.',
    pulse: false,
  },
] as const;

function ExplainerStep({
  onDone,
  busy,
  error,
}: {
  onDone: () => void;
  busy: boolean;
  error: string | null;
}) {
  const platform = here();
  const desktop = platform === 'other' ? null : DESKTOP[platform];
  const rise = (index: number) => ({ animationDelay: `${index * 90}ms`, animationFillMode: 'both' as const });

  return (
    <section className="card overflow-hidden">
      <div className="p-5 sm:p-6">
        <div className="flex items-center gap-4 animate-fade-up" style={rise(0)}>
          <img src={appIcon} alt="" width={64} height={64} className="h-16 w-16 drop-shadow-[0_8px_24px_rgba(0,0,0,0.45)]" />
          <div>
            <p className="text-caption uppercase tracking-wide text-chalk-faint">Step 2 of 2</p>
            <h1 className="mt-1 text-h2 text-chalk">How Slate works</h1>
          </div>
        </div>

        <ol className="mt-6 grid gap-3 sm:grid-cols-3">
          {HOW.map((step, index) => (
            <li
              key={step.title}
              className="animate-fade-up rounded-md border border-hairline bg-slate-900/60 p-4"
              style={rise(index + 1)}
            >
              <span className={cx('relative flex h-9 w-9 items-center justify-center rounded-full', step.ring, step.tone)}>
                {step.pulse ? <span className="absolute inset-0 animate-record-pulse rounded-full bg-record/20" aria-hidden /> : null}
                <span className="relative">{step.icon}</span>
              </span>
              <p className="mt-3 text-body font-medium text-chalk">{step.title}</p>
              <p className="mt-1 text-caption text-chalk-dim">{step.body}</p>
            </li>
          ))}
        </ol>
      </div>

      <div
        className="grid items-center gap-5 border-t border-hairline bg-gradient-to-br from-slate-900 to-slate-950 p-5 sm:grid-cols-[minmax(0,1fr)_200px] sm:p-6 animate-fade-up"
        style={rise(4)}
      >
        <div>
          <p className="text-body font-medium text-chalk">{desktop ? desktop.title : 'Slate is wherever you are'}</p>
          <p className="mt-1 text-caption text-chalk-dim">
            {desktop
              ? desktop.body
              : 'This browser records, with nothing to install. There are also apps for Mac and Windows, an extension for Chrome, and iPhone and Android.'}
          </p>
          <p className="mt-2 text-caption text-chalk-faint">
            {desktop ? 'Also a Chrome extension, this browser, and your phone. ' : ''}
            <a href={DOWNLOADS} target="_blank" rel="noopener noreferrer" className="text-signal hover:underline">
              See every way to record
            </a>
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {desktop ? (
              <a href={DOWNLOADS} target="_blank" rel="noopener noreferrer">
                <Button type="button" variant="primary" size="lg" className="gap-2">
                  <DownloadGlyph />
                  {desktop.cta}
                </Button>
              </a>
            ) : null}
            <Button
              type="button"
              variant={desktop ? 'ghost' : 'primary'}
              size="lg"
              busy={busy}
              onClick={onDone}
            >
              {desktop ? 'Continue to your library' : 'Continue'}
            </Button>
          </div>
          {error ? (
            <div className="mt-3">
              <ErrorText>{error}</ErrorText>
            </div>
          ) : null}
        </div>
        <img
          src={macPanel}
          alt="The Slate panel: a screen to pick, camera and microphone, and a Start recording button"
          width={200}
          height={218}
          className="mx-auto w-[200px] rounded-lg shadow-[0_18px_40px_rgba(0,0,0,0.5)] ring-1 ring-white/10"
        />
      </div>
    </section>
  );
}

function DownloadGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 3v12" />
      <path d="M7 10l5 5 5-5" />
      <path d="M4 20h16" />
    </svg>
  );
}
